import axiosInstance from "../utils/axios";
import appErrorToast from "../components/common/AppErrorToast";
import type {
  ICreateResourceAllocationOrderPayload,
  IResourceAllocationEmployeeOptionApi,
  IResourceAllocationOrderApi,
  IResourceAllocationOrdersResponseApi,
  IResourceAllocationProjectApi,
  IResourceAllocationSummaryResponse,
  IResourceAllocationSummaryRowApi,
  IUpdateResourceAllocationOrderPayload,
} from "./types/resourceAllocation";
import type { WorktimeSettlementStatus } from "./types/worktimeSettlement";
import { getResourceAllocationApiView } from "../utils/resourceAllocationView";

const pathPrefix = `${process.env.REACT_APP_SERVER_URL}/api`;

function mapSettlementStatus(
  value: string | number | null | undefined
): WorktimeSettlementStatus | null {
  if (value === null || value === undefined) return null;

  const normalized = String(value).toLowerCase();
  if (normalized === "pending" || normalized === "0") return "pending";
  if (normalized === "rejected" || normalized === "1") return "rejected";
  if (normalized === "accepted" || normalized === "2") return "accepted";
  return null;
}

function mapOrderSettlementStatus(
  value: string | number | null | undefined
): WorktimeSettlementStatus {
  return mapSettlementStatus(value) ?? "pending";
}

function mapSummaryRow(row: IResourceAllocationSummaryRowApi): IResourceAllocationSummaryRowApi {
  return {
    ...row,
    settlementStatus: mapSettlementStatus(row.settlementStatus as unknown as string),
    rejectionReason: row.rejectionReason ?? null,
    rejectionDetails: row.rejectionDetails ?? [],
  };
}

function mapOrder(row: IResourceAllocationOrderApi): IResourceAllocationOrderApi {
  return {
    ...row,
    managerFullName: row.managerFullName ?? undefined,
    managerId: row.managerId ?? undefined,
    settlementStatus: mapOrderSettlementStatus(row.settlementStatus as unknown as string),
  };
}

function viewQuery(): string {
  return `view=${encodeURIComponent(getResourceAllocationApiView())}`;
}

export async function getResourceAllocationSummary(
  year: number,
  month: number,
  employeeId?: string
): Promise<IResourceAllocationSummaryResponse> {
  try {
    const employeeQuery = employeeId ? `&employeeId=${encodeURIComponent(employeeId)}` : "";
    const response = await axiosInstance.get<IResourceAllocationSummaryResponse>(
      `${pathPrefix}/resource-allocation/summary?year=${year}&month=${month}${employeeQuery}&${viewQuery()}`
    );
    const data = response.data;
    return {
      ...data,
      readOnlyMode: data.readOnlyMode ?? getResourceAllocationApiView() === "admin",
      rows: (data.rows ?? []).map(mapSummaryRow),
    };
  } catch (error) {
    appErrorToast(`Błąd pobierania alokacji zasobów: ${error}`);
    return { year, month, readOnlyMode: getResourceAllocationApiView() === "admin", rows: [] };
  }
}

export async function getResourceAllocationFilterEmployees(): Promise<IResourceAllocationEmployeeOptionApi[]> {
  try {
    const response = await axiosInstance.get<IResourceAllocationEmployeeOptionApi[]>(
      `${pathPrefix}/resource-allocation/filter-employees?${viewQuery()}`
    );
    return response.data ?? [];
  } catch (error) {
    appErrorToast(`Błąd pobierania listy pracowników: ${error}`);
    return [];
  }
}

export async function getResourceAllocationProjects(
  employeeId: string
): Promise<IResourceAllocationProjectApi[]> {
  try {
    const response = await axiosInstance.get<IResourceAllocationProjectApi[]>(
      `${pathPrefix}/resource-allocation/employees/${employeeId}/projects?${viewQuery()}`
    );
    return response.data ?? [];
  } catch (error) {
    appErrorToast(`Błąd pobierania projektów: ${error}`);
    return [];
  }
}

export async function getResourceAllocationOrders(
  employeeId: string,
  year: number,
  month: number,
  projectId?: string
): Promise<IResourceAllocationOrdersResponseApi> {
  try {
    const projectQuery = projectId ? `&projectId=${encodeURIComponent(projectId)}` : "";
    const response = await axiosInstance.get<IResourceAllocationOrdersResponseApi>(
      `${pathPrefix}/resource-allocation/employees/${employeeId}/orders?year=${year}&month=${month}${projectQuery}&${viewQuery()}`
    );
    const data = response.data;
    return {
      orders: (data.orders ?? []).map(mapOrder),
      isMainSettlementAccepted: data.isMainSettlementAccepted ?? false,
    };
  } catch (error) {
    appErrorToast(`Błąd pobierania zleceń: ${error}`);
    return { orders: [], isMainSettlementAccepted: false };
  }
}

export async function createResourceAllocationOrder(
  employeeId: string,
  payload: ICreateResourceAllocationOrderPayload
): Promise<void> {
  try {
    await axiosInstance.post(
      `${pathPrefix}/resource-allocation/employees/${employeeId}/orders`,
      payload
    );
  } catch (error) {
    appErrorToast(`Błąd tworzenia zlecenia: ${error}`);
    throw error;
  }
}

export async function updateResourceAllocationOrder(
  employeeId: string,
  orderId: string,
  payload: IUpdateResourceAllocationOrderPayload
): Promise<void> {
  try {
    await axiosInstance.put(
      `${pathPrefix}/resource-allocation/employees/${employeeId}/orders/${orderId}`,
      payload
    );
  } catch (error) {
    appErrorToast(`Błąd edycji zlecenia: ${error}`);
    throw error;
  }
}

export async function deleteResourceAllocationOrder(
  employeeId: string,
  orderId: string
): Promise<void> {
  try {
    await axiosInstance.delete(
      `${pathPrefix}/resource-allocation/employees/${employeeId}/orders/${orderId}`
    );
  } catch (error) {
    appErrorToast(`Błąd usuwania zlecenia: ${error}`);
    throw error;
  }
}

export async function acceptResourceAllocationSettlement(
  employeeId: string,
  year: number,
  month: number
): Promise<void> {
  try {
    await axiosInstance.put(
      `${pathPrefix}/resource-allocation/employees/${employeeId}/settlement/accept?year=${year}&month=${month}`
    );
  } catch (error) {
    appErrorToast(`Błąd podczas akceptacji rozliczenia: ${error}`);
    throw error;
  }
}

export async function rejectResourceAllocationSettlement(
  employeeId: string,
  year: number,
  month: number,
  rejectionReason: string
): Promise<void> {
  try {
    await axiosInstance.put(
      `${pathPrefix}/resource-allocation/employees/${employeeId}/settlement/reject?year=${year}&month=${month}`,
      { rejectionReason }
    );
  } catch (error) {
    appErrorToast(`Błąd podczas odrzucania rozliczenia: ${error}`);
    throw error;
  }
}
